import { useMemo, useRef, useState } from "react";
import { Alert, Button, Col, Form, Input, InputNumber, Modal, Row, Select, Space, Tag, Tooltip, Typography } from "antd";
import { RiseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import moment from "moment";
import ApiService from "../../core/service/api-service";
import DatePicker from "../../core/components/DatePicker";
import { LocalStorageService } from "../../core/service/local-storage-service";
import { dateFormat } from "../../core/utils/date-utils";
import { PATIENT_CLASSES, formatNumber, increasedLimit } from "./contract-utils";

const DEFAULT_INCREASE_PERCENT = 20;

// Çip seçici (Form.Item içinde value/onChange ile çalışır).
function ChipGroup({ value = [], onChange, items }) {
    const toggle = (key, checked) => onChange?.(checked ? [...value, key] : value.filter(x => x !== key));

    return (
        <Space size={[4, 4]} wrap>
            {items.map(x => {
                const tag = (
                    <Tag.CheckableTag
                        key={x.value}
                        checked={value.includes(x.value)}
                        onChange={(checked) => toggle(x.value, checked)}
                        style={{ border: '1px solid #d9d9d9', padding: '2px 10px' }}
                    >
                        {x.label}
                    </Tag.CheckableTag>
                );
                return x.tooltip ? <Tooltip key={x.value} title={x.tooltip}>{tag}</Tooltip> : tag;
            })}
        </Space>
    )
}

/**
 * Sözleşme ekle/düzenle.
 *
 * Tek seferlik artırım yalnız kayıtlı sözleşmede ve ayrı uçla uygulanır; uygulandıktan sonra kilitlenir.
 */
export default function ContractEditModal({ contract, options, onClose, onSaved, onIncreased }) {
    const { t } = useTranslation();
    const formRef = useRef();

    const [saving, setSaving] = useState(false)
    const [increasing, setIncreasing] = useState(false)
    const [increasePercent, setIncreasePercent] = useState(DEFAULT_INCREASE_PERCENT)

    const initialValues = useMemo(() => contract
        ? {
            hospitalId: contract.hospitalId,
            name: contract.name,
            reportLimit: contract.reportLimit,
            warningPercent: contract.warningPercent,
            startDate: moment(contract.startDate, 'YYYY-MM-DD'),
            endDate: moment(contract.endDate, 'YYYY-MM-DD'),
            modalityIdList: contract.modalityIdList,
            studyTypeList: contract.studyTypeList,
        }
        : {
            warningPercent: 80,
            modalityIdList: [],
            studyTypeList: [],
        }, []);

    const save = async (form) => {
        setSaving(true)
        try {
            const res = await ApiService.HospitalContract.Save({
                id: contract?.id ?? null,
                hospitalId: form.hospitalId,
                name: form.name,
                reportLimit: form.reportLimit,
                warningPercent: form.warningPercent,
                startDate: form.startDate.format('YYYY-MM-DD'),
                endDate: form.endDate.format('YYYY-MM-DD'),
                modalityIdList: form.modalityIdList,
                studyTypeList: form.studyTypeList,
            });
            if (res.success) {
                await onSaved?.()
                onClose()
            }
        } finally {
            setSaving(false)
        }
    }

    const applyIncrease = () => {
        Modal.confirm({
            title: t('One-time report count increase'),
            content: `${formatNumber(contract.reportLimit)} → ${formatNumber(increasedLimit(contract.reportLimit, increasePercent))}. ${t('This can be applied only once and cannot be undone.')}`,
            okText: t('Apply'),
            cancelText: t('Cancel'),
            onOk: async () => {
                setIncreasing(true)
                try {
                    const res = await ApiService.HospitalContract.ApplyIncrease({ id: contract.id, percent: increasePercent });
                    if (res.success)
                        onIncreased?.(res.data)
                } finally {
                    setIncreasing(false)
                }
            }
        });
    }

    const required = [{ required: true, message: t('Required') }];

    return (
        <Modal
            open={true}
            title={contract ? t('Edit Contract') : t('New Contract')}
            onCancel={onClose}
            width={640}
            destroyOnClose
            footer={[
                <Button key={'cancel'} onClick={onClose}>{t('Cancel')}</Button>,
                <Button key={'save'} type={'primary'} loading={saving} onClick={() => formRef.current.submit()}>{t('Save')}</Button>,
            ]}
        >
            <Form
                ref={formRef}
                layout={'vertical'}
                initialValues={initialValues}
                onFinish={save}
            >
                <Form.Item name={'hospitalId'} label={t('Hospital')} rules={required}>
                    <Select
                        showSearch
                        optionFilterProp={'label'}
                        options={options.hospitals.map(x => ({ value: x.id, label: x.name }))}
                    />
                </Form.Item>

                <Form.Item name={'name'} label={t('Contract Name')} rules={required}>
                    <Input maxLength={255} />
                </Form.Item>

                <Row gutter={12}>
                    <Col span={12}>
                        <Form.Item name={'reportLimit'} label={t('Report count')} rules={required}>
                            <InputNumber min={1} precision={0} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name={'warningPercent'} label={t('Warning Level (%)')} rules={required}>
                            <InputNumber min={1} max={100} precision={0} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>

                {contract && (
                    <div style={{ border: '1px solid #d9d9d9', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                        <Typography.Text strong>{t('One-time report count increase')}</Typography.Text>
                        <div>
                            <Typography.Text type={'secondary'}>{t('An increase can be applied to this contract only once.')}</Typography.Text>
                        </div>
                        {contract.increasePercent != null
                            ? (
                                <Alert
                                    style={{ marginTop: 8 }}
                                    type={'info'}
                                    showIcon
                                    message={`+%${contract.increasePercent}: ${formatNumber(contract.reportLimit)} → ${formatNumber(contract.effectiveLimit)}`}
                                    description={[contract.increasedUserName, contract.increasedAt && dateFormat(contract.increasedAt)].filter(Boolean).join(' · ')}
                                />
                            )
                            : (
                                <>
                                    <Space style={{ marginTop: 8 }}>
                                        <InputNumber
                                            min={1}
                                            max={100}
                                            precision={0}
                                            addonAfter={'%'}
                                            value={increasePercent}
                                            onChange={(value) => setIncreasePercent(value)}
                                        />
                                        <Button
                                            type={'primary'}
                                            icon={<RiseOutlined />}
                                            loading={increasing}
                                            disabled={!increasePercent}
                                            onClick={applyIncrease}
                                        >
                                            {t('Apply')}
                                        </Button>
                                    </Space>
                                    <div>
                                        <Typography.Text type={'secondary'}>
                                            {formatNumber(contract.reportLimit)} → <b>{formatNumber(increasedLimit(contract.reportLimit, increasePercent))}</b>
                                        </Typography.Text>
                                    </div>
                                </>
                            )}
                    </div>
                )}

                <Row gutter={12}>
                    <Col span={12}>
                        <Form.Item name={'startDate'} label={t('Start Date')} rules={required}>
                            <DatePicker locale={LocalStorageService.getAntLocale()} format={'DD.MM.YYYY'} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name={'endDate'}
                            label={t('End Date')}
                            dependencies={['startDate']}
                            rules={[
                                ...required,
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        const start = getFieldValue('startDate');
                                        if (!value || !start || !value.isBefore(start, 'day'))
                                            return Promise.resolve();
                                        return Promise.reject(new Error(t('End date cannot be before start date')));
                                    }
                                })
                            ]}
                        >
                            <DatePicker locale={LocalStorageService.getAntLocale()} format={'DD.MM.YYYY'} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    name={'modalityIdList'}
                    label={t('Modality / Device')}
                    rules={[{ required: true, type: 'array', min: 1, message: t('Select at least one modality') }]}
                >
                    <ChipGroup items={options.modalities.map(x => ({ value: x.id, label: x.name }))} />
                </Form.Item>

                <Form.Item
                    name={'studyTypeList'}
                    label={t('Patient Classes')}
                    rules={[{ required: true, type: 'array', min: 1, message: t('Select at least one patient class') }]}
                >
                    <ChipGroup items={PATIENT_CLASSES.map(x => ({ value: x.value, label: x.code, tooltip: t(x.label) }))} />
                </Form.Item>
            </Form>
        </Modal>
    )
}
